(() => {
  if (window.__tanUniversalV37) return;
  window.__tanUniversalV37 = true;

  const sent = new Set();
  const allowedKinds = new Set(['page', 'policy']);
  const allowedModes = new Set(['short', 'long']);
  const allowedRoutes = new Set(['policy_navigation', 'contact', 'help_center', 'contents', 'print']);
  const designMode = () => document.documentElement.classList.contains('shopify-design-mode');
  const clean = (value, allowed, fallback = 'other') => allowed.has(String(value || '')) ? String(value) : fallback;

  const publish = (name, data = {}, onceKey = '') => {
    if (designMode()) return;
    if (onceKey && sent.has(onceKey)) return;
    if (onceKey) sent.add(onceKey);
    const payload = {
      event_version: '1.0',
      language_code: String(document.documentElement.lang || '').slice(0, 20),
      ...data,
    };
    window.dispatchEvent(new CustomEvent('tan:universal-event', { detail: { name, data: payload } }));
    try {
      window.Shopify?.analytics?.publish?.(`the_art_neon:${name}`, payload);
    } catch (_) {
      // The information page remains fully usable when analytics is unavailable.
    }
  };

  const bodyFor = (root) => root.querySelector('[data-tan-universal-body]') || root.querySelector('.shopify-policy__body .rte');

  const uniqueHeadingId = (index, reserved) => {
    let number = index + 1;
    let id = `information-section-${number}`;
    while (reserved.has(id)) {
      number += 1;
      id = `information-section-${number}`;
    }
    return id;
  };

  const wrapTables = (root, body) => {
    body.querySelectorAll('table').forEach((table) => {
      if (table.closest('.tan-universal-table-scroll')) return;
      const wrapper = document.createElement('div');
      wrapper.className = 'tan-universal-table-scroll';
      wrapper.tabIndex = 0;
      wrapper.setAttribute('role', 'region');
      wrapper.setAttribute('aria-label', root.dataset.tableLabel || '');
      table.before(wrapper);
      wrapper.append(table);
    });
  };

  const buildContents = (root, body) => {
    const lists = Array.from(root.querySelectorAll('[data-tan-toc-list]'));
    if (lists.length === 0) return;

    const headings = Array.from(body.querySelectorAll('h2, h3')).filter((heading) => heading.textContent?.trim());
    const textLength = body.textContent?.trim().length || 0;
    const isLong = headings.length >= 3 || (headings.length >= 2 && textLength >= 1200);
    const mode = isLong ? 'long' : 'short';
    root.dataset.contentMode = mode;
    if (!isLong) return;

    const reserved = new Set(Array.from(document.querySelectorAll('[id]')).map((element) => element.id).filter(Boolean));
    const usedHeadingIds = new Set();

    headings.forEach((heading, index) => {
      let id = heading.id;
      if (!id || usedHeadingIds.has(id)) {
        id = uniqueHeadingId(index, reserved);
        heading.id = id;
      }
      usedHeadingIds.add(id);
      reserved.add(id);
      if (!heading.hasAttribute('tabindex')) heading.tabIndex = -1;

      lists.forEach((list) => {
        const item = document.createElement('li');
        item.dataset.level = heading.tagName === 'H3' ? '3' : '2';
        const link = document.createElement('a');
        link.href = `#${encodeURIComponent(id)}`;
        link.textContent = heading.textContent.trim();
        link.addEventListener('click', () => {
          publish('universal_navigation_used', {
            page_kind: clean(root.dataset.pageKind, allowedKinds),
            content_mode: clean(root.dataset.contentMode, allowedModes),
            route_type: 'contents',
          });
          window.setTimeout(() => heading.focus({ preventScroll: true }), 0);
        });
        item.append(link);
        list.append(item);
      });
    });

    root.querySelectorAll('[data-tan-toc-shell], [data-tan-toc-mobile]').forEach((element) => {
      element.hidden = false;
    });

    root.querySelectorAll('[data-tan-toc-mobile]').forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        publish('universal_contents_opened', {
          page_kind: clean(root.dataset.pageKind, allowedKinds),
          content_mode: 'long',
        }, `contents-opened-${clean(root.dataset.pageKind, allowedKinds)}`);
      });
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (!visible) return;
        const activeHash = `#${encodeURIComponent(visible.target.id)}`;
        lists.forEach((list) => {
          list.querySelectorAll('a').forEach((link) => {
            if (link.getAttribute('href') === activeHash) link.setAttribute('aria-current', 'location');
            else link.removeAttribute('aria-current');
          });
        });
      }, { rootMargin: '-18% 0px -70% 0px', threshold: 0 });
      headings.forEach((heading) => observer.observe(heading));
    }
  };

  const bindPrint = (root) => {
    root.querySelectorAll('[data-tan-print]:not([data-tan-print-bound])').forEach((button) => {
      button.dataset.tanPrintBound = 'true';
      button.hidden = false;
      button.addEventListener('click', () => {
        publish('universal_navigation_used', {
          page_kind: clean(root.dataset.pageKind, allowedKinds),
          content_mode: clean(root.dataset.contentMode, allowedModes, 'short'),
          route_type: 'print',
        });
        window.print();
      });
    });
  };

  const bindRoutes = (root) => {
    root.querySelectorAll('[data-tan-universal-route]:not([data-tan-universal-bound])').forEach((link) => {
      link.dataset.tanUniversalBound = 'true';
      link.addEventListener('click', () => {
        publish('universal_navigation_used', {
          page_kind: clean(root.dataset.pageKind, allowedKinds),
          content_mode: clean(root.dataset.contentMode, allowedModes, 'short'),
          route_type: clean(link.dataset.routeType, allowedRoutes),
        });
      });
    });
  };

  const revealCurrentPolicy = (root) => {
    const current = root.querySelector('.tan-universal-policy-nav__link[aria-current="page"]');
    if (!current) return;
    window.requestAnimationFrame(() => {
      const links = current.closest('.tan-universal-policy-nav__links');
      if (!links || links.scrollWidth <= links.clientWidth + 1) return;
      current.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
    });
  };

  const initializeRoot = (root) => {
    if (!(root instanceof Element) || root.dataset.tanUniversalReady === 'true') return;
    root.dataset.tanUniversalReady = 'true';
    const body = bodyFor(root);
    if (body) {
      wrapTables(root, body);
      buildContents(root, body);
    } else {
      root.dataset.contentMode = 'short';
    }
    bindPrint(root);
    bindRoutes(root);
    revealCurrentPolicy(root);
  };

  const initialize = (scope = document) => {
    const roots = [];
    if (scope instanceof Element && scope.matches('[data-tan-universal-root]')) roots.push(scope);
    scope.querySelectorAll?.('[data-tan-universal-root]').forEach((root) => roots.push(root));
    roots.forEach(initializeRoot);
  };

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
